import React from 'react'
import { createTheme, ThemeProvider } from '@mui/material'
import CssBaseline from '@mui/material/CssBaseline'

import { useTheme } from '../contexts/ThemeContext'

type MuiThemeProviderProps = {
  children: React.ReactNode
}

/**
 * Material-UI ThemeProvider that syncs with our custom ThemeContext
 * Provides proper dark mode support for all Material-UI components
 */
export const MuiThemeProvider = ({
  children,
}: MuiThemeProviderProps): React.JSX.Element => {
  const { isDark } = useTheme()

  const theme = createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary: {
        main: isDark ? '#60A5FA' : '#3B82F6', // Blue-400 in dark mode, Blue-500 in light mode
        light: isDark ? '#93C5FD' : '#60A5FA', // Blue-300 in dark mode, Blue-400 in light mode
        dark: isDark ? '#3B82F6' : '#1D4ED8', // Blue-500 in dark mode, Blue-700 in light mode
      },
      secondary: {
        main: '#13BC6B', // brand Emerald (logo green) — brand-green-500
        light: '#2cde88', // brand-green-400
        dark: '#0c864c', // brand-green-700
      },
      warning: {
        main: '#F59E0B', // Amber-500
        light: '#FCD34D', // Amber-300
        dark: '#D97706', // Amber-600
      },
      background: {
        default: isDark ? '#0F172A' : '#FFFFFF', // slate-900 : white
        paper: isDark ? '#1E293B' : '#FFFFFF', // slate-800 : white
      },
      text: {
        primary: isDark ? '#FFFFFF' : '#0F172A', // white : slate-900
        secondary: isDark ? '#94A3B8' : '#64748B', // slate-400 : slate-500
      },
      divider: isDark ? '#334155' : '#E2E8F0', // slate-700 : slate-200
    },
    components: {
      // Base button component (affects all button-like components)
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
          disableTouchRipple: true,
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none', // Disable ALL CAPS text transformation
          },
          containedPrimary: {
            backgroundColor: isDark ? '#60A5FA' : '#3B82F6', // Same as primary.main
            color: isDark ? '#000000' : '#FFFFFF', // Black text in dark mode, white in light mode
            fontWeight: 600, // Bold text for primary buttons
            '&:hover': {
              backgroundColor: isDark ? '#3B82F6' : '#1D4ED8', // Same as primary.dark
            },
            '&:disabled': {
              backgroundColor: isDark ? '#374151' : '#E5E7EB', // gray-700 : gray-200
              color: isDark ? '#6B7280' : '#9CA3AF', // gray-500 : gray-400
              fontWeight: 400, // Normal weight when disabled
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            // Tight global tab metrics — compact padding/height applied to
            // every MUI tab bar app-wide (paired with MuiTabs.root minHeight
            // below). Individual tab bars can still override via sx.
            minHeight: 36,
            minWidth: 0,
            padding: '6px 12px',
            color: isDark ? '#94A3B8' : '#64748B', // slate-400 : slate-500
            '&.Mui-selected': {
              color: '#3B82F6', // blue-500
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: '14px', // Consistent 14px font size
            minHeight: '40px', // Consistent menu item height
            color: isDark ? '#F3F4F6' : '#1F2937', // gray-100 : gray-800
            '& .MuiListItemIcon-root': {
              minWidth: '36px', // Icon spacing
            },
            '&:hover': {
              backgroundColor: isDark ? '#4B5563' : '#F3F4F6', // gray-600 : gray-100
            },
            '&.Mui-selected': {
              backgroundColor: isDark ? 'rgba(59, 130, 246, 0.2)' : '#EFF6FF', // blue with opacity : blue-50
              color: isDark ? '#DBEAFE' : '#1D4ED8', // blue-200 : blue-700
              fontWeight: 600,
              '&:hover': {
                backgroundColor: isDark ? 'rgba(59, 130, 246, 0.3)' : '#DBEAFE', // blue-200
              },
            },
          },
        },
      },
      // TextField/Input styling
      MuiTextField: {
        styleOverrides: {
          root: {
            // Global search and filter inputs get full width
            '&.MuiTextField-fullWidth': {
              maxWidth: 'none !important',
              width: '100%',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            fontSize: '14px', // Consistent 14px font size
            maxWidth: 'none !important', // Allow full width inputs
            '& fieldset': {
              borderColor: isDark ? '#475569' : '#E5E7EB', // Use gray-200 from docs for consistency
            },
            '&:hover fieldset': {
              borderColor: isDark ? '#64748B' : '#D1D5DB', // Use gray-300 from docs
            },
            '&.Mui-focused fieldset': {
              borderColor: '#3B82F6', // blue-500
              borderWidth: '2px',
            },
            backgroundColor: isDark ? '#131c2c' : '#FFFFFF',
            // Border radius variants
            '&.border-radius-6': {
              borderRadius: '6px',
              '& fieldset': {
                borderRadius: '6px',
              },
            },
            '&.border-radius-8': {
              borderRadius: '8px',
              '& fieldset': {
                borderRadius: '8px',
              },
            },
            // Header search variant - no border except on focus
            '&.header-search': {
              '& fieldset': {
                border: 'none',
              },
              '&:hover fieldset': {
                border: 'none',
              },
              '&.Mui-focused fieldset': {
                border: '2px solid #3B82F6',
                borderRadius: '8px',
              },
            },
          },
          input: {
            fontSize: '14px', // Consistent 14px font size
            color: isDark ? '#FFFFFF' : '#1F2937', // white : gray-800
            '&::placeholder': {
              color: isDark ? '#9CA3AF' : '#6B7280', // gray-400 : gray-500
              opacity: 1,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: isDark ? '#9CA3AF' : '#6B7280', // gray-400 : gray-500
            '&.Mui-focused': {
              color: '#3B82F6', // blue-500
            },
          },
        },
      },
      // FormControl styling for filter controls
      MuiFormControl: {
        styleOverrides: {
          root: {
            '&.filter-control': {
              maxWidth: '300px',
              fontSize: '14px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
              },
            },
          },
        },
      },
      // Select dropdown styling
      MuiSelect: {
        styleOverrides: {
          root: {
            fontSize: '14px', // Consistent 14px font size
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? '#475569' : '#E5E7EB', // Use gray-200 from docs
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? '#64748B' : '#D1D5DB', // Use gray-300 from docs
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#3B82F6', // blue-500
              borderWidth: '2px',
            },
            // Border radius variants
            '&.border-radius-6': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderRadius: '6px',
              },
            },
          },
        },
      },
      // Menu/dropdown paper styling
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#374151' : '#FFFFFF', // gray-700 : white
            backgroundImage: 'none',
          },
        },
      },
      // Input adornment (icons)
      MuiInputAdornment: {
        styleOverrides: {
          root: {
            color: isDark ? '#9CA3AF' : '#6B7280', // gray-400 : gray-500
            // Icon size variants
            '& .MuiSvgIcon-root': {
              '&.icon-size-16': {
                fontSize: '16px',
              },
              '&.icon-size-20': {
                fontSize: '20px',
              },
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 36, // match the tight MuiTab height above
          },
          indicator: {
            backgroundColor: '#3B82F6', // blue-500
            height: '2px',
          },
        },
      },
      // Global SvgIcon styling for consistent icon sizes
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            '&.icon-size-16': {
              fontSize: '16px',
            },
            '&.icon-size-20': {
              fontSize: '20px',
            },
          },
        },
      },
    },
    typography: {
      fontFamily: '"Noto Sans", ui-sans-serif, system-ui, sans-serif',
    },
  })

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  )
}
